<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { permissionManager, PERMISSIONS, ROLES } from "@/utils/permission";

export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "",
      items: [],
      loading: false,
      activeTab: 'roles',
      
      // 角色管理
      roles: [],
      selectedRole: null,
      roleDialog: false,
      roleForm: {
        id: null,
        name: '',
        description: '',
        permissions: []
      },
      
      // 权限管理
      permissionTree: [],
      allPermissions: [],
      
      // 用户角色分配
      users: [],
      userRoleDialog: false,
      selectedUser: null,
      userRoleForm: {
        userId: null,
        roleId: null
      },
      userinfo: {},
      roleDialogKey: 0,
      // 门店可管理设备（仅超级管理员配置，写入 store_user_devices 表）
      storeDeviceOptions: [],
      storeDeviceMap: {},
      storeAssignUserId: null,
      storeAssignEdIds: [],
      storeTabLoading: false
    };
  },
  computed: {
    /**
     * 与侧栏「门店设备分配」一致：需 permission.edit。
     * 超管/管理员（roleid 0/1）可分配设备到门店权限账号。
     */
    canAssignStoreDevices() {
      const rid = parseInt(
        this.$permissionState?.roleId != null
          ? this.$permissionState.roleId
          : this.userinfo.roleid != null
            ? this.userinfo.roleid
            : this.userinfo.roleId,
        10
      );
      if (rid === 0 || rid === 1) {
        return true;
      }
      return permissionManager.hasPermission("permission.edit");
    },
    storeStaffUsers() {
      return (this.users || []).filter((u) => {
        const rid = parseInt(
          u.roleId != null ? u.roleId : u.roleid != null ? u.roleid : u.effective_role_id,
          10
        );
        return rid === 4;
      });
    }
  },
  watch: {
    activeTab(val) {
      if (val === "storeDevices" && this.canAssignStoreDevices) {
        this.loadStoreDevicesTab();
      }
    },
    "$route"(to) {
      if (to.path === "/setting/permission") {
        this.applyStoreTabFromRoute();
      }
    }
  },
  mounted() {
    this.title = this.$t("permission.title");
    this.items = [
      {
        text: this.$t("menuitems.setting.text"),
        href: "/setting/system",
      },
      {
        text: this.$t("permission.title"),
        active: true,
      },
    ];
    try {
      this.userinfo = JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch (e) {
      this.userinfo = {};
    }
    this.loadPermissions();
    this.bootstrapPermissionData().then(() => {
      this.applyStoreTabFromRoute();
    });
  },
  activated() {
    this.applyStoreTabFromRoute();
  },
  methods: {
    /**
     * 从 ?tab=storeDevices 切换页签（含：从侧栏进入、已在权限页时再次点菜单）
     */
    applyStoreTabFromRoute() {
      if (this.$route.query.tab !== "storeDevices") {
        return;
      }
      if (!this.canAssignStoreDevices) {
        this.activeTab = "roles";
        this.$message.warning(this.$t("permission.store.noAccess"));
        return;
      }
      this.$nextTick(() => {
        this.activeTab = "storeDevices";
      });
    },
    /**
     * 从后台加载角色与管理员列表（配置保存在数据库 roles / user_permissions）
     */
    async bootstrapPermissionData() {
      await this.fetchRolesFromApi();
      await this.fetchUsersFromApi();
      this.loadRoles();
    },
    fetchRolesFromApi() {
      if (!this.userinfo.openid || !this.apiuri) {
        return Promise.resolve();
      }
      return this.$axios
        .post(
          this.apiuri,
          { action: "getroles" },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((res) => {
          if (res.data && res.data.status === 200 && res.data.data) {
            permissionManager.applyServerRoles(res.data.data);
          }
        })
        .catch(() => {
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    fetchUsersFromApi() {
      if (!this.userinfo.openid || !this.apiuri) {
        this.loadUsersFallback();
        return Promise.resolve();
      }
      return this.$axios
        .post(
          this.apiuri,
          { action: "getpermissionusers" },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((res) => {
          if (res.data && res.data.status === 200 && Array.isArray(res.data.data)) {
            this.users = res.data.data.map((u) => ({
              id: u.id,
              username: u.username,
              roleId: u.effective_role_id,
              roleName: this.roleNameForId(u.effective_role_id),
            }));
          } else {
            this.loadUsersFallback();
          }
        })
        .catch(() => {
          this.loadUsersFallback();
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    roleNameForId(roleId) {
      const list = permissionManager.getAllRoles();
      const hit = list.find((r) => r.id === roleId);
      return hit ? hit.name : String(roleId);
    },
    loadUsersFallback() {
      this.users = [
        { id: 1, username: "admin", roleId: 0, roleName: "超级管理员" },
        { id: 2, username: "manager", roleId: 1, roleName: "管理员" },
        { id: 3, username: "operator", roleId: 2, roleName: "操作员" },
        { id: 4, username: "viewer", roleId: 3, roleName: "只读用户" },
      ];
    },
    openAddRole() {
      this.roleForm = {
        id: null,
        name: "",
        description: "",
        permissions: [],
      };
      this.roleDialogKey += 1;
      this.roleDialog = true;
    },
    /**
     * 加载角色列表
     */
    loadRoles() {
      this.roles = permissionManager.getAllRoles().map(role => ({
        ...role,
        userCount: this.getRoleUserCount(role.id)
      }));
    },
    
    /**
     * 加载权限列表
     */
    loadPermissions() {
      this.allPermissions = permissionManager.getAllPermissions();
      this.buildPermissionTree();
    },
    
    /**
     * 构建权限树
     */
    buildPermissionTree() {
      const groups = [
        {
          key: "permission.groups.device",
          perms: ["device.view", "device.edit", "device.delete", "device.add"],
        },
        {
          key: "permission.groups.order",
          perms: ["order.view", "order.edit", "order.delete", "order.print"],
        },
        {
          key: "permission.groups.user",
          perms: ["user.view", "user.edit", "user.delete", "user.add"],
        },
        {
          key: "permission.groups.admin",
          perms: ["admin.view", "admin.edit", "admin.delete", "admin.add"],
        },
        {
          key: "permission.groups.goods",
          perms: ["goods.view", "goods.edit", "goods.delete", "goods.add"],
        },
        {
          key: "permission.groups.system",
          perms: ["system.view", "system.edit"],
        },
        {
          key: "permission.groups.marketing",
          perms: ["marketing.view", "marketing.edit"],
        },
        {
          key: "permission.groups.wechat",
          perms: ["wechat.view", "wechat.edit"],
        },
        {
          key: "permission.groups.rbac",
          perms: ["permission.view", "permission.edit", "permission.delete"],
        },
      ];
      this.permissionTree = groups.map(({ key, perms }) => ({
        id: key,
        children: perms.map((permission) => ({
          id: permission,
        })),
      }));
    },

    /**
     * 权限树节点展示文案（分组与子权限均走 permission.* 命名空间）
     */
    permissionNodeLabel(data) {
      if (!data || !data.id) {
        return "";
      }
      const id = data.id;
      const i18nKey = id.startsWith("permission.groups.")
        ? id
        : `permission.${id}`;
      const text = this.$t(i18nKey);
      return text === i18nKey ? id : text;
    },
    
    /**
     * 加载用户列表（已由 fetchUsersFromApi 填充；无 API 时用 loadUsersFallback）
     */
    loadUsers() {
      if (!this.users || this.users.length === 0) {
        this.loadUsersFallback();
      }
    },
    
    /**
     * 获取角色用户数量
     */
    getRoleUserCount(roleId) {
      return this.users.filter(user => user.roleId === roleId).length;
    },
    
    /**
     * 选择角色
     */
    selectRole(role) {
      this.selectedRole = role;
      this.roleForm = {
        id: role.id,
        name: role.name,
        description: role.description || '',
        permissions: [...role.permissions]
      };
    },
    
    /**
     * 编辑角色
     */
    editRole(role) {
      this.selectRole(role);
      this.roleDialogKey += 1;
      this.roleDialog = true;
    },
    
    /**
     * 从 el-tree 勾选结果中提取权限 code 列表（排除分组节点）
     */
    normalizePermissionKeys(keys) {
      if (!Array.isArray(keys)) {
        return [];
      }
      return keys.filter(
        (k) =>
          typeof k === "string" &&
          k.includes(".") &&
          !k.startsWith("permission.groups.")
      );
    },
    /**
     * 权限变更处理（el-tree @check 第一参数为节点对象，第二参数含 checkedKeys）
     */
    onPermissionChange(data, checkedInfo) {
      const keys =
        checkedInfo && Array.isArray(checkedInfo.checkedKeys)
          ? checkedInfo.checkedKeys
          : [];
      this.roleForm.permissions = this.normalizePermissionKeys(keys);
    },
    
    /**
     * 保存角色
     */
    saveRole() {
      if (!this.roleForm.name) {
        this.$message.error(this.$t("permission.ui.roleNameRequired"));
        return;
      }
      if (!this.userinfo.openid || !this.apiuri) {
        this.$message.error(this.$t("permission.ui.apiMissing"));
        return;
      }
      this.loading = true;
      const payload = {
        action: "saverole",
        id: this.roleForm.id,
        name: this.roleForm.name,
        description: this.roleForm.description || "",
        permissions: this.normalizePermissionKeys(this.roleForm.permissions),
      };
      this.$axios
        .post(this.apiuri, payload, {
          headers: { Openid: this.userinfo.openid },
        })
        .then((res) => {
          this.loading = false;
          if (res.data && res.data.status === 200) {
            this.roleDialog = false;
            this.bootstrapPermissionData();
            this.$message.success(this.$t("permission.ui.roleSaveSuccess"));
          } else {
            this.$message.error(
              (res.data && res.data.msg) || this.$t("permission.ui.saveFailed")
            );
          }
        })
        .catch(() => {
          this.loading = false;
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    
    /**
     * 删除角色
     */
    deleteRole(role) {
      if (role.id === 0 || role.id === 4) {
        this.$message.error(this.$t("permission.ui.cannotDeletePreset"));
        return;
      }
      
      this.$confirm(this.$t("permission.ui.deleteRoleConfirm"), this.$t("global.waring"), {
        confirmButtonText: this.$t("global.button.ok"),
        cancelButtonText: this.$t("global.button.cancel"),
        type: 'warning'
      }).then(() => {
        if (!this.userinfo.openid || !this.apiuri) {
          this.$message.error(this.$t("permission.ui.apiMissing"));
          return;
        }
        this.loading = true;
        this.$axios
          .post(
            this.apiuri,
            { action: "deleterole", id: role.id },
            { headers: { Openid: this.userinfo.openid } }
          )
          .then((res) => {
            this.loading = false;
            if (res.data && res.data.status === 200) {
              if (this.selectedRole && this.selectedRole.id === role.id) {
                this.selectedRole = null;
              }
              this.bootstrapPermissionData();
              this.$message.success(this.$t("permission.ui.roleDeleteSuccess"));
            } else {
              this.$message.error(
                (res.data && res.data.msg) || this.$t("permission.ui.deleteFailed")
              );
            }
          })
          .catch(() => {
            this.loading = false;
            this.$message.error(this.$t("global.errors.network") || "网络错误");
          });
      });
    },
    
    /**
     * 分配用户角色
     */
    assignUserRole(user) {
      this.selectedUser = user;
      this.userRoleForm = {
        userId: user.id,
        roleId: user.roleId
      };
      this.userRoleDialog = true;
    },
    
    /**
     * 保存用户角色
     */
    saveUserRole() {
      if (
        this.userRoleForm.userId == null ||
        this.userRoleForm.roleId == null
      ) {
        this.$message.error(this.$t("permission.ui.selectRoleRequired"));
        return;
      }
      if (!this.userinfo.openid || !this.apiuri) {
        this.$message.error(this.$t("permission.ui.apiMissing"));
        return;
      }
      this.loading = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "setadminrole",
            user_id: this.userRoleForm.userId,
            role_id: this.userRoleForm.roleId,
          },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((res) => {
          this.loading = false;
          if (res.data && res.data.status === 200) {
            this.userRoleDialog = false;
            this.bootstrapPermissionData();
            this.$message.success(this.$t("permission.ui.userRoleSaveSuccess"));
          } else {
            this.$message.error(
              (res.data && res.data.msg) || this.$t("permission.ui.assignFailed")
            );
          }
        })
        .catch(() => {
          this.loading = false;
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    
    /**
     * 获取角色标签类型
     */
    getRoleTagType(roleId) {
      const types = {
        0: 'danger',
        1: 'primary',
        2: 'warning',
        3: 'info',
        4: 'success'
      };
      return types[roleId] || 'info';
    },
    loadStoreDevicesTab() {
      if (!this.userinfo.openid || !this.apiuri || !this.canAssignStoreDevices) {
        return;
      }
      this.storeTabLoading = true;
      const headers = { Openid: this.userinfo.openid };
      Promise.all([
        this.$axios.post(
          this.apiuri,
          { action: "getdevicelist" },
          { headers }
        ),
        this.$axios.post(
          this.apiuri,
          { action: "getstoredevices" },
          { headers }
        ),
      ])
        .then(([devRes, mapRes]) => {
          this.storeTabLoading = false;
          if (devRes.data && devRes.data.status === 200 && devRes.data.data) {
            this.storeDeviceOptions = devRes.data.data;
          } else {
            this.storeDeviceOptions = [];
          }
          if (mapRes.data && mapRes.data.status === 200 && mapRes.data.data) {
            this.storeDeviceMap = { ...mapRes.data.data };
          } else {
            this.storeDeviceMap = {};
          }
          this.syncStoreAssignFromMap();
        })
        .catch(() => {
          this.storeTabLoading = false;
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    syncStoreAssignFromMap() {
      if (!this.storeAssignUserId) {
        this.storeAssignEdIds = [];
        return;
      }
      const key = String(this.storeAssignUserId);
      const list = this.storeDeviceMap[key];
      this.storeAssignEdIds = Array.isArray(list) ? [...list] : [];
    },
    onStoreUserChange() {
      this.syncStoreAssignFromMap();
    },
    saveStoreDeviceAssign() {
      if (!this.storeAssignUserId) {
        this.$message.error(this.$t("permission.store.selectUser"));
        return;
      }
      if (!this.userinfo.openid || !this.apiuri) {
        return;
      }
      this.storeTabLoading = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "setstoredevices",
            user_id: this.storeAssignUserId,
            ed_ids: this.storeAssignEdIds,
          },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((res) => {
          this.storeTabLoading = false;
          if (res.data && res.data.status === 200) {
            this.storeDeviceMap[String(this.storeAssignUserId)] = [
              ...this.storeAssignEdIds,
            ];
            this.$message.success(this.$t("permission.store.saveOk"));
          } else {
            this.$message.error(
              res.data && res.data.msg === "not_store_user"
                ? this.$t("permission.store.notStoreUser")
                : this.$t("global.errors.loaddata")
            );
          }
        })
        .catch(() => {
          this.storeTabLoading = false;
          this.$message.error(this.$t("global.errors.network") || "网络错误");
        });
    },
    
    /**
     * 获取角色权限列表
     */
    getRolePermissions(roleId) {
      return permissionManager.getRolePermissions(roleId);
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <!-- 权限检查 -->
        <div v-if="!$hasPermission('permission.view')" class="card">
          <div class="card-body text-center">
            <el-result
              icon="error"
              :title="$t('global.error')"
              :subTitle="$t('global.text.nopermission')"
            >
              <template slot="extra">
                <el-button type="primary" size="medium" @click="$router.go(-1)">
                  {{ $t('global.button.back') }}
                </el-button>
              </template>
            </el-result>
          </div>
        </div>

        <!-- 主要内容 -->
        <div v-else>
          <!-- 标签页 -->
          <div class="card mb-4">
            <div class="card-body">
              <el-tabs v-model="activeTab" type="card">
                <el-tab-pane :label="$t('permission.tabs.roles')" name="roles">
                  <!-- 角色列表 -->
                  <div class="row mb-3">
                    <div class="col-12">
                      <div class="d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">{{ $t("permission.ui.roleListHeading") }}</h5>
                        <el-button 
                          type="primary" 
                          @click="openAddRole"
                          v-if="$hasPermission('permission.edit')"
                        >
                          <i class="ri-add-line me-1"></i>
                          {{ $t("permission.roles.add") }}
                        </el-button>
                      </div>
                    </div>
                  </div>
                  
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead class="table-light">
                        <tr>
                          <th>{{ $t("permission.roles.name") }}</th>
                          <th>{{ $t("permission.table.description") }}</th>
                          <th>{{ $t("permission.roles.userCount") }}</th>
                          <th>{{ $t("permission.roles.permissionCount") }}</th>
                          <th>{{ $t("permission.table.action") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr 
                          v-for="role in roles" 
                          :key="role.id"
                          @click="selectRole(role)"
                          style="cursor: pointer"
                        >
                          <td>
                            <strong>{{ role.name }}</strong>
                            <el-tag 
                              v-if="role.id === 0 || role.id === 4" 
                              type="danger" 
                              size="small" 
                              class="ms-2"
                            >
                              {{ $t("permission.ui.systemRole") }}
                            </el-tag>
                          </td>
                          <td>{{ role.description || '-' }}</td>
                          <td>
                            <el-badge :value="role.userCount" type="primary">
                              <span>{{ $t("permission.ui.userBadge") }}</span>
                            </el-badge>
                          </td>
                          <td>
                            <el-badge :value="role.permissions.length" type="success">
                              <span>{{ $t("permission.ui.permBadge") }}</span>
                            </el-badge>
                          </td>
                          <td>
                            <el-button 
                              size="mini" 
                              @click.stop="editRole(role)"
                              v-if="$hasPermission('permission.edit')"
                            >
                              {{ $t("permission.roles.edit") }}
                            </el-button>
                            <el-button 
                              size="mini" 
                              type="danger" 
                              @click.stop="deleteRole(role)"
                              v-if="$hasPermission('permission.delete') && role.id !== 0 && role.id !== 4"
                            >
                              {{ $t("permission.roles.delete") }}
                            </el-button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </el-tab-pane>
                
                <el-tab-pane
                  v-if="canAssignStoreDevices"
                  :label="$t('permission.store.tab')"
                  name="storeDevices"
                >
                  <div class="row mb-3">
                    <div class="col-12">
                      <p class="text-muted small mb-2">
                        {{ $t("permission.store.hint") }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="row g-3 align-items-end"
                    v-loading="storeTabLoading"
                  >
                    <div class="col-md-4">
                      <label class="form-label">{{ $t("permission.store.storeUser") }}</label>
                      <el-select
                        v-model="storeAssignUserId"
                        filterable
                        clearable
                        :placeholder="$t('permission.store.selectUser')"
                        style="width: 100%"
                        @change="onStoreUserChange"
                      >
                        <el-option
                          v-for="u in storeStaffUsers"
                          :key="u.id"
                          :label="u.username"
                          :value="u.id"
                        />
                      </el-select>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">{{ $t("permission.store.devices") }}</label>
                      <el-select
                        v-model="storeAssignEdIds"
                        multiple
                        filterable
                        collapse-tags
                        :placeholder="$t('permission.store.selectDevices')"
                        style="width: 100%"
                      >
                        <el-option
                          v-for="d in storeDeviceOptions"
                          :key="d.ED_id"
                          :label="(d.name || d.ED_id) + ' (' + d.ED_id + ')'"
                          :value="d.ED_id"
                        />
                      </el-select>
                    </div>
                    <div class="col-md-2">
                      <el-button
                        type="primary"
                        class="w-100"
                        :disabled="!storeAssignUserId"
                        @click="saveStoreDeviceAssign"
                      >
                        {{ $t("permission.store.save") }}
                      </el-button>
                    </div>
                  </div>
                  <p v-if="storeStaffUsers.length === 0" class="text-warning mt-3 small">
                    {{ $t("permission.store.noStoreUsers") }}
                  </p>
                </el-tab-pane>

                <el-tab-pane :label="$t('permission.tabs.users')" name="users">
                  <!-- 用户角色分配 -->
                  <div class="row mb-3">
                    <div class="col-12">
                      <h5 class="mb-0">{{ $t("permission.ui.userAssignHeading") }}</h5>
                    </div>
                  </div>
                  
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead class="table-light">
                        <tr>
                          <th>{{ $t("permission.users.username") }}</th>
                          <th>{{ $t("permission.users.currentRole") }}</th>
                          <th>{{ $t("permission.users.permissionCount") }}</th>
                          <th>{{ $t("permission.table.action") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="user in users" :key="user.id">
                          <td>
                            <strong>{{ user.username }}</strong>
                            <el-tag 
                              v-if="user.id === userinfo.id" 
                              type="danger" 
                              size="small" 
                              class="ms-2"
                            >
                              {{ $t("permission.ui.currentAccount") }}
                            </el-tag>
                          </td>
                          <td>
                            <el-tag :type="getRoleTagType(user.roleId)">
                              {{ user.roleName }}
                            </el-tag>
                          </td>
                          <td>
                            <el-badge :value="getRolePermissions(user.roleId).length" type="info">
                              <span>{{ $t("permission.ui.permBadge") }}</span>
                            </el-badge>
                          </td>
                          <td>
                            <el-button 
                              size="mini" 
                              @click="assignUserRole(user)"
                              v-if="$hasPermission('permission.edit') && user.id !== userinfo.id"
                            >
                              {{ $t("permission.users.assignRole") }}
                            </el-button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </div>
          </div>

          <!-- 权限配置面板 -->
          <div v-if="selectedRole" class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">
                {{ $t("permission.ui.configTitle", { name: selectedRole.name }) }}
              </h5>
            </div>
            <div class="card-body">
              <el-tree
                :key="selectedRole.id"
                :data="permissionTree"
                show-checkbox
                node-key="id"
                :default-checked-keys="selectedRole.permissions"
                @check="onPermissionChange"
                :props="{ children: 'children', label: 'id' }"
              >
                <span slot-scope="{ data }">{{ permissionNodeLabel(data) }}</span>
              </el-tree>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 角色编辑对话框 -->
    <el-dialog
      :title="roleForm.id == null ? $t('permission.ui.dialogAddRole') : $t('permission.ui.dialogEditRole')"
      :visible.sync="roleDialog"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form :model="roleForm" label-width="100px">
        <el-form-item :label="$t('permission.roles.name')">
          <el-input v-model="roleForm.name" :placeholder="$t('permission.ui.roleNamePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('permission.table.description')">
          <el-input 
            v-model="roleForm.description" 
            type="textarea" 
            :placeholder="$t('permission.ui.roleDescPlaceholder')"
            :rows="3"
          />
        </el-form-item>
        <el-form-item :label="$t('permission.ui.permConfigLabel')">
          <el-tree
            :key="'role-dlg-' + roleDialogKey"
            :data="permissionTree"
            show-checkbox
            node-key="id"
            :default-checked-keys="roleForm.permissions"
            @check="onPermissionChange"
            :props="{ children: 'children', label: 'id' }"
          >
            <span slot-scope="{ data }">{{ permissionNodeLabel(data) }}</span>
          </el-tree>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="roleDialog = false">{{ $t("global.button.cancel") }}</el-button>
        <el-button type="primary" @click="saveRole" :loading="loading">{{ $t("global.button.save") }}</el-button>
      </div>
    </el-dialog>

    <!-- 用户角色分配对话框 -->
    <el-dialog
      :title="$t('permission.ui.assignUserRoleTitle')"
      :visible.sync="userRoleDialog"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form :model="userRoleForm" label-width="100px">
        <el-form-item :label="$t('permission.ui.usernameLabel')">
          <el-input :value="selectedUser && selectedUser.username" disabled />
        </el-form-item>
        <el-form-item :label="$t('permission.ui.roleLabel')">
          <el-select v-model="userRoleForm.roleId" :placeholder="$t('permission.ui.selectRolePlaceholder')" style="width: 100%">
            <el-option
              v-for="role in roles"
              :key="role.id"
              :label="role.name"
              :value="role.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="userRoleDialog = false">{{ $t("global.button.cancel") }}</el-button>
        <el-button type="primary" @click="saveUserRole" :loading="loading">{{ $t("global.button.save") }}</el-button>
      </div>
    </el-dialog>
  </Layout>
</template>

<style scoped>
.table tbody tr:hover {
  background-color: #f8f9fa;
}

.el-tree {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 1rem;
}

.el-badge {
  margin-right: 0.5rem;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}
</style>
